/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from 'react'
import clsx from 'clsx'
import CompanyFormLabel from 'pages/Front/Booking/components/CompanyForm/CompanyFormLabel'
import {useTranslation} from 'react-i18next'
import T from 'components/T'
import {
	AtSymbolIcon,
	BuildingOffice2Icon,
	ChatBubbleBottomCenterTextIcon,
	EnvelopeOpenIcon,
	MapIcon,
	MapPinIcon,
	PhoneIcon,
	RectangleStackIcon,
	UserIcon,
	UsersIcon,
} from '@heroicons/react/24/outline'
import {getAutoComplete} from 'utils/autoCompleteUtils'
import {FieldErrors, UseFormRegister} from 'react-hook-form'
import UiInput from 'components/Ui/UiInput'
import { uiFormControlDisabledClassName } from 'components/Ui/UiFormControlStyles'

interface CompanyFormInputProps {
    errors: FieldErrors
    register: UseFormRegister<any>
    name: string
    label?: React.ReactNode
    type?: string
    className?: string
    classNameLabel?: string
    placeholder?: string
    required?: boolean
    disabled?: boolean
    title?: string
    iconName?: string
    iconPosition?: 'left' | 'right'
    component?: 'input' | 'select' | 'textarea'
    options?: { value: string; label: string }[]
    registerOptions?: any
    containerClassName?: string
    inputClassName?: string
    inputStyle?: React.CSSProperties
    isTextArea?: boolean
    rows?: number
    autoComplete?: string
    inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode']
}

const CompanyFormInput = (props: CompanyFormInputProps) => {
    const { t } = useTranslation()
    const iconClass = 'h-5 w-5 text-black/40'
    const iconPosition = props.iconPosition || 'left'

    const baseClasses = clsx(
        'w-full h-10 py-2 mt-0 border rounded-lg text-sm transition-colors',
        props.iconName && iconPosition === 'left' ? 'pl-10 pr-3' : props.iconName && iconPosition === 'right' ? 'pl-3 pr-10' : 'px-3',
        props.errors?.[props.name]
            ? 'border-red-500 focus:border-red-500'
            : 'border-gray-300',
        props.disabled
            ? uiFormControlDisabledClassName
            : 'shadow-xs bg-white focus:outline-none focus:border-primary-600 checked:bg-cta-800 dark:bg-gray-950 dark:text-gray-100 dark:border-gray-800 dark:focus:border-primary-600',
        props.inputClassName
    )

    const renderField = () => {
        if (props.component === 'select') {
            return (
                <select
                    {...props.register(props.name, props.registerOptions)}
                    disabled={props.disabled}
                    className={baseClasses}
                    id={'input-' + props.name}
                    style={props.inputStyle}
                >
                    {props.options?.map((opt) => (
                        <option
                            className={clsx('text-2xl')}
                            key={opt.value}
                            value={opt.value}
                        >
                            {opt.label}
                        </option>
                    ))}
                </select>
            )
        }

        if (props.component === 'textarea' || props.isTextArea) {
            return (
                <textarea
                    {...props.register(props.name, props.registerOptions)}
                    disabled={props.disabled}
                    placeholder={props.placeholder && t(props.placeholder)}
                    className={clsx(baseClasses, 'h-auto resize-none')}
                    id={'input-' + props.name}
                    style={props.inputStyle}
                    rows={props.rows || 3}
                />
            )
        }

        return (
            <UiInput
                {...props.register(props.name, props.registerOptions)}
                autoComplete={props.autoComplete || getAutoComplete(props.name)}
                className={clsx(
                    'mt-0 shadow-xs',
                    props.iconName && iconPosition === 'left' ? 'pl-10 pr-3' : props.iconName && iconPosition === 'right' ? 'pl-3 pr-10' : 'px-3'
                )}
                hasError={Boolean(props.errors?.[props.name])}
                disabled={props.disabled}
                id={'input-' + props.name}
                placeholder={props.placeholder && t(props.placeholder)}
                type={props.type ? props.type : 'text'}
                inputMode={props.inputMode}
                style={props.inputStyle}
            />
        )
    }

    return (
        <div
            className={clsx(
                'CompanyFormInput',
                props.className,
                props.containerClassName
            )}
            title={props.title}
        >
            {props.label && (
                <CompanyFormLabel
                    className={clsx('mb-1.5', props.classNameLabel)}
                    errors={props.errors}
                    label={props.label}
                    name={props.name}
                    required={props.required}
                />
            )}

            <div className={clsx('relative ')}>
                {props.iconName && iconPosition === 'left' && (
                    <div
                        className={clsx(
                            'absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-20'
                        )}
                    >
                        {props.iconName === 'company_name' && (
                            <BuildingOffice2Icon className={iconClass} />
                        )}
                        {props.iconName === 'cin' && (
                            <RectangleStackIcon className={iconClass} />
                        )}
                        {props.iconName === 'street_address' && (
                            <MapPinIcon className={iconClass} />
                        )}
                        {props.iconName === 'city' && (
                            <MapIcon className={iconClass} />
                        )}
                        {props.iconName === 'postal_code' && (
                            <EnvelopeOpenIcon className={iconClass} />
                        )}
                        {props.iconName === 'first_name' && (
                            <UserIcon className={iconClass} />
                        )}
                        {props.iconName === 'name' && (
                            <UserIcon className={iconClass} />
                        )}
                        {props.iconName === 'last_name' && (
                            <UsersIcon className={iconClass} />
                        )}
                        {props.iconName === 'email' && (
                            <AtSymbolIcon className={iconClass} />
                        )}
                        {props.iconName === 'phone' && (
                            <PhoneIcon className={iconClass} />
                        )}
                        {props.iconName === 'message' && (
                            <ChatBubbleBottomCenterTextIcon
                                className={iconClass}
                            />
                        )}
                    </div>
                )}

                {renderField()}


                {props.iconName && iconPosition === 'right' && (
                    <div
                        className={clsx(
                            'absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none z-20'
                        )}
                    >
                        {props.iconName === 'company_name' && (
                            <BuildingOffice2Icon className={iconClass} />
                        )}
                        {props.iconName === 'cin' && (
                            <RectangleStackIcon className={iconClass} />
                        )}
                        {props.iconName === 'street_address' && (
                            <MapPinIcon className={iconClass} />
                        )}
                        {props.iconName === 'city' && (
                            <MapIcon className={iconClass} />
                        )}
                        {props.iconName === 'postal_code' && (
                            <EnvelopeOpenIcon className={iconClass} />
                        )}
                        {props.iconName === 'first_name' && (
                            <UserIcon className={iconClass} />
                        )}
                        {props.iconName === 'name' && (
                            <UserIcon className={iconClass} />
                        )}
                        {props.iconName === 'last_name' && (
                            <UsersIcon className={iconClass} />
                        )}
                        {props.iconName === 'email' && (
                            <AtSymbolIcon className={iconClass} />
                        )}
                        {props.iconName === 'phone' && (
                            <PhoneIcon className={iconClass} />
                        )}
                        {props.iconName === 'message' && (
                            <ChatBubbleBottomCenterTextIcon
                                className={iconClass}
                            />
                        )}
                    </div>
                )}
            </div>

            {props.errors?.[props.name]?.message && (
                <div
                    className={clsx('text-red-500 text-xs mt-1 flex flex-row')}
                >
                    <T>{String(props.errors?.[props.name]?.message)}</T>
                </div>
            )}
        </div>
    )
}

export default CompanyFormInput
